import React, { createContext, useContext, useEffect, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import { $patchStyleText, $setBlocksType } from "@lexical/selection";
import { createEmptyHistoryState } from "@lexical/history";
import { $createLineBreakNode, $createParagraphNode, $createTextNode, $getNodeByKey, $getRoot, $getSelection, $isElementNode, $isRangeSelection, $setSelection, DecoratorNode, FORMAT_ELEMENT_COMMAND, FORMAT_TEXT_COMMAND, REDO_COMMAND, UNDO_COMMAND } from "lexical";
import { readContentRuns } from "./FormContentEditor";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Italic, Redo2, Underline, Undo2 } from "lucide-react";
import "./FormDocumentEditor.css";

const DocumentContext = createContext({ fields: [] });
const buttonClass = "rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100";

function moveNode(node, direction) {
  const sibling = direction < 0 ? node.getPreviousSibling() : node.getNextSibling();
  if (!sibling) return;
  if (direction < 0) sibling.insertBefore(node);
  else sibling.insertAfter(node);
}

function Placeholder({ nodeKey, block }) {
  const [editor] = useLexicalComposerContext();
  const { fields } = useContext(DocumentContext);
  const field = fields.find((item) => item.key === block.field_key);
  const label = block.type === "field" ? field?.label || block.field_key : block.type === "pricing" ? "Service pricing table" : block.type === "agreement" ? "Consent agreement checkbox" : "Patient / guardian signature";
  const act = (operation) => editor.update(() => {
    const node = $getNodeByKey(nodeKey);
    if (node) operation(node);
  });
  return <div className="my-3 rounded border border-dashed border-cyan-500 bg-cyan-50 p-4" draggable
    onDragStart={(event) => { event.stopPropagation(); event.dataTransfer.setData("application/doclift-placeholder", nodeKey); }}
    onDragOver={(event) => { if (event.dataTransfer.types.includes("application/doclift-placeholder")) { event.preventDefault(); event.stopPropagation(); } }}
    onDrop={(event) => {
      const key = event.dataTransfer.getData("application/doclift-placeholder");
      if (!key || key === nodeKey) return;
      event.preventDefault();
      event.stopPropagation();
      act((target) => { const source = $getNodeByKey(key); if (source && source.getParent()?.getKey() === target.getParent()?.getKey()) target.insertBefore(source); });
    }}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <strong className="text-sm text-cyan-950">{label}</strong>
      <div className="flex gap-1">
        <button type="button" className={buttonClass} aria-label={`Move ${label} up`} onClick={() => act((node) => moveNode(node, -1))}>Up</button>
        <button type="button" className={buttonClass} aria-label={`Move ${label} down`} onClick={() => act((node) => moveNode(node, 1))}>Down</button>
        <button type="button" className={buttonClass} aria-label={`Remove ${label}`} onClick={() => act((node) => node.remove())}>Remove</button>
      </div>
    </div>
    <p className="mt-2 text-xs text-slate-600">{block.type === "pricing" ? "Services, discounts and total are filled from the catalog when sending. Patients cannot edit prices." : block.type === "agreement" ? "The patient must tick this checkbox before submitting. Edit its wording in Agreement and signature text." : block.type === "signature" ? "The patient draws their signature here. Add a Consent agreement checkbox section separately if acceptance is needed." : `${field?.type || "text"} answer${field?.required ? " (required)" : ""}. Configure the question in Field settings.`}</p>
    {block.type === "agreement" && <div className="mt-3 flex items-center gap-2 text-xs text-slate-700"><span className="inline-block h-4 w-4 border border-slate-400 bg-white" aria-hidden="true" /> I have read and agree…</div>}
    {block.type === "pricing" && <div className="mt-3 grid grid-cols-4 gap-2 border-t border-cyan-200 pt-2 text-xs"><span>Service / tooth</span><span>Cost</span><span>Discount</span><span>Net cost / total</span></div>}
    {block.type === "signature" && <div className="mt-3 h-20 border border-slate-300 bg-white" />}
  </div>;
}

export class FormPlaceholderNode extends DecoratorNode {
  static getType() { return "form-placeholder"; }
  static clone(node) { return new FormPlaceholderNode(node.__block, node.__key); }
  static importJSON(serialized) { return new FormPlaceholderNode(serialized.block); }
  constructor(block, key) { super(key); this.__block = { ...block }; }
  exportJSON() { return { ...super.exportJSON(), type: "form-placeholder", version: 1, block: this.__block }; }
  createDOM() { return document.createElement("div"); }
  updateDOM() { return false; }
  isInline() { return false; }
  getTextContent() { return `[${this.__block.type}]`; }
  decorate() { return <Placeholder nodeKey={this.__key} block={this.__block} />; }
}

export function readDocument() {
  return $getRoot().getChildren().flatMap((node) => {
    if (node instanceof FormPlaceholderNode) return [{ ...node.__block }];
    const runs = readContentRuns(node, false);
    const text = runs.map((run) => run.text).join("");
    if (!text) return [];
    return [{ type: $isHeadingNode(node) ? "heading" : "text", ...($isHeadingNode(node) ? { bold: false } : {}), text, runs, alignment: node.getFormatType() || "left" }];
  });
}

function writeDocument(blocks) {
  const root = $getRoot();
  root.clear();
  blocks.forEach((block) => {
    if (!["text", "heading"].includes(block.type)) { root.append(new FormPlaceholderNode(block)); return; }
    const node = block.type === "heading" ? $createHeadingNode("h2") : $createParagraphNode();
    node.setFormat(block.alignment || "left");
    (block.runs || [{ text: block.text || "", bold: block.bold ?? block.type === "heading" }]).forEach((original) => {
      const run = { ...original, bold: block.bold === true || original.bold, italic: block.italic || original.italic, color: original.color || block.color, font_size: original.font_size || block.font_size };
      run.text.split("\n").forEach((text, index) => {
        if (index) node.append($createLineBreakNode());
        if (!text) return;
        const child = $createTextNode(text);
        for (const format of ["bold", "italic", "underline"]) if (run[format]) child.toggleFormat(format);
        child.setStyle(`${run.color ? `color: ${run.color};` : ""}${run.font_size ? `font-size: ${run.font_size}px;` : ""}`);
        node.append(child);
      });
    });
    root.append(node);
  });
  root.append($createParagraphNode());
}

function DocumentControls({ blocks, fields, onChange }) {
  const [editor] = useLexicalComposerContext();
  const fieldsRef = useRef(fields);
  const fieldRegistry = useRef(new Map());
  const callback = useRef(onChange);
  const emitted = useRef(JSON.stringify(blocks));
  const selectionRef = useRef(null);
  const history = useRef(null);
  if (!history.current) {
    history.current = createEmptyHistoryState();
  }
  useEffect(() => {
    history.current.current = { editor, editorState: editor.getEditorState() };
  }, [editor]);
  fieldsRef.current = fields;
  fields.forEach((field) => fieldRegistry.current.set(field.key, field));
  callback.current = onChange;
  useEffect(() => editor.registerUpdateListener(({ editorState }) => editorState.read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) selectionRef.current = selection.clone();
  })), [editor]);
  useEffect(() => {
    if (JSON.stringify(blocks) === emitted.current) return;
    editor.update(() => writeDocument(blocks), { tag: "external-document" });
    emitted.current = JSON.stringify(blocks);
  }, [editor, blocks]);
  const update = (action) => editor.update(() => {
    if (selectionRef.current && $getNodeByKey(selectionRef.current.anchor.key) && $getNodeByKey(selectionRef.current.focus.key)) $setSelection(selectionRef.current.clone());
    action($getSelection());
  });
  const insert = (value) => update((selection) => {
    if (!value) return;
    if (value.startsWith("token:")) {
      if ($isRangeSelection(selection)) selection.insertText(`{{${value.slice(6)}}}`);
      else $getRoot().append($createParagraphNode().append($createTextNode(`{{${value.slice(6)}}}`)));
      return;
    }
    let block;
    if (value.startsWith("new:")) {
      const kind = value.slice(4);
      let number = 1;
      while (fieldRegistry.current.has(`field_${number}`)) number += 1;
      const field = {
        key: `field_${number}`, label: kind === "parent" ? "Parent name" : kind === "guardian" ? "Guardian name" : kind === "relationship" ? "Relationship with patient" : kind === "consent" ? "I agree to the consent" : "New question",
        type: kind === "relationship" ? "select" : kind === "consent" ? "checkbox" : "text",
        required: kind !== "parent", patient_editable: true, validation: {},
        ...(kind === "parent" ? { customer_lookup: true } : {}),
        options: kind === "relationship" ? ["Self", "Parent", "Guardian", "Spouse"].map((label) => ({ label, value: label.toLowerCase() })) : [],
      };
      fieldsRef.current = [...fieldsRef.current, field];
      fieldRegistry.current.set(field.key, field);
      block = { type: "field", field_key: field.key };
    } else block = { type: value };
    if (block.type !== "field" && readDocument().some((item) => item.type === block.type)) return;
    const node = new FormPlaceholderNode(block);
    if ($isRangeSelection(selection)) selection.insertNodes([node]);
    else $getRoot().append(node);
    if (!$isElementNode(node.getNextSibling())) node.insertAfter($createParagraphNode());
    node.getNextSibling().selectStart();
  });
  return <>
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 p-3" role="toolbar" aria-label="Document formatting">
      {[["bold", "Bold", Bold], ["italic", "Italic", Italic], ["underline", "Underline", Underline]].map(([format, label, Icon]) => <button key={format} type="button" title={label} aria-label={label} className={buttonClass} onMouseDown={(event) => event.preventDefault()} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)}><Icon size={16} aria-hidden="true" /></button>)}
      <select aria-label="Paragraph type" className={buttonClass} value="" onChange={(event) => { const type = event.target.value; update((selection) => { if ($isRangeSelection(selection)) $setBlocksType(selection, () => type === "heading" ? $createHeadingNode("h2") : $createParagraphNode()); }); }}><option value="">Paragraph style</option><option value="text">Text</option><option value="heading">Heading</option></select>
      <select aria-label="Document font size" className={buttonClass} defaultValue="" onChange={(event) => { const size = event.target.value; update((selection) => { if ($isRangeSelection(selection)) $patchStyleText(selection, { "font-size": size ? `${size}px` : null }); }); }}><option value="">Font size</option>{[12, 14, 16, 18, 20, 24, 28, 32, 36].map((size) => <option key={size} value={size}>{size} px</option>)}</select>
      <input type="color" aria-label="Document text color" defaultValue="#111111" className="h-7 w-7" onChange={(event) => { const color = event.target.value; update((selection) => { if ($isRangeSelection(selection)) $patchStyleText(selection, { color }); }); }} />
      {[["left", "Align left", AlignLeft], ["center", "Align center", AlignCenter], ["right", "Align right", AlignRight], ["justify", "Justify", AlignJustify]].map(([alignment, label, Icon]) => <button type="button" key={alignment} title={label} aria-label={label} className={buttonClass} onMouseDown={(event) => event.preventDefault()} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment)}><Icon size={16} aria-hidden="true" /></button>)}
      {[-1, 1].map((direction) => <button key={direction} type="button" className={buttonClass} onMouseDown={(event) => event.preventDefault()} onClick={() => update((selection) => { const node = $isRangeSelection(selection) ? selection.anchor.getNode().getTopLevelElement() : null; if (node) moveNode(node, direction); })}>Move paragraph {direction < 0 ? "up" : "down"}</button>)}
      <select aria-label="Insert placeholder" className={buttonClass} value="" onChange={(event) => insert(event.target.value)}>
        <option value="">Insert placeholder</option>
        <optgroup label="Patient and clinic">{["patient.name", "patient.title", "patient.name_with_title", "patient.gender", "patient.relationship_to_guardian", "patient.phone", "patient.email", "patient.id", "clinic.name", "pricing.total"].map((token) => <option key={token} value={`token:${token}`}>{token}</option>)}</optgroup>
        <optgroup label="Consent signer">{["guardian.name", "guardian.relationship"].map((token) => <option key={token} value={`token:${token}`}>{token}</option>)}</optgroup>
        <optgroup label="Document sections"><option value="pricing" disabled={blocks.some((block) => block.type === "pricing")}>Service pricing table</option><option value="agreement" disabled={blocks.some((block) => block.type === "agreement")}>Consent agreement checkbox</option><option value="signature" disabled={blocks.some((block) => block.type === "signature")}>Signature</option></optgroup>
        <optgroup label="Questions" disabled={fields.length >= 100}><option value="new:parent">Parent name (search records)</option><option value="new:guardian">Guardian name</option><option value="new:relationship">Relationship with patient</option><option value="new:consent">Consent checkbox</option><option value="new:question">Custom question</option></optgroup>
        <optgroup label="Answer text">{fields.map((field) => <option key={field.key} value={`token:${field.key}`}>{field.label || field.key}</option>)}</optgroup>
      </select>
      <button type="button" title="Undo" aria-label="Undo" className={buttonClass} onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}><Undo2 size={16} aria-hidden="true" /></button>
      <button type="button" title="Redo" aria-label="Redo" className={buttonClass} onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}><Redo2 size={16} aria-hidden="true" /></button>
    </div>
    <RichTextPlugin contentEditable={<ContentEditable aria-label="Form document" className="form-document-editor min-h-[600px] whitespace-pre-wrap break-words bg-white p-6 text-sm leading-7 text-slate-900 outline-none sm:p-10" />} ErrorBoundary={LexicalErrorBoundary} />
    <HistoryPlugin externalHistoryState={history.current} />
    <OnChangePlugin ignoreSelectionChange onChange={(state, instance, tags) => {
      if (tags.has("external-document")) return;
      state.read(() => {
        const result = readDocument();
        emitted.current = JSON.stringify(result);
        const keys = new Set(result.filter((block) => block.type === "field").map((block) => block.field_key));
        const nextFields = Array.from(keys, (key) => fieldRegistry.current.get(key)).filter(Boolean);
        callback.current(result, nextFields);
      });
    }} />
  </>;
}

export default function FormDocumentEditor({ blocks, fields, onChange }) {
  const initial = useRef(blocks);
  return <DocumentContext.Provider value={{ fields }}>
    <div className="overflow-hidden rounded-lg border border-slate-300 bg-white">
      <LexicalComposer initialConfig={{ namespace: "FormDocument", nodes: [HeadingNode, FormPlaceholderNode], onError: (error) => { throw error; }, editorState: () => writeDocument(initial.current), theme: { heading: { h2: "text-lg font-normal" }, text: { bold: "font-bold", italic: "italic", underline: "underline" } } }}>
        <DocumentControls blocks={blocks} fields={fields} onChange={onChange} />
      </LexicalComposer>
    </div>
  </DocumentContext.Provider>;
}
